import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Box, Collapse, TableCell, TableRow } from "@mui/material";
import { toast } from "react-toastify";
import ActionButton from "../../shared/ActionButton";
import ConfirmStopModal from "../../shared/ConfirmStopModal";
import { JOB_COLOR, JOB_COLOR_DARK } from "../../../constants/colors";
import { FONT_SIZE } from "../../../constants/fontSizes";
import CompanyUserSummary from "./CompanyUserSummary";
import CompanyUserList from "./CompanyUserList";
import {
  buildFullName,
  buildInitialCompanyUsers,
  validateCompanyUsers,
} from "./companyUserUtils";

// 共通ボタンスタイル
const buttonBaseSx = {
  height: 32,
  minWidth: 96,
  width: "auto",
  fontSize: FONT_SIZE.SM,
  boxShadow: "none",
  pr: 3,
  pl: 2.5,
};

// トースト表示用のエラーメッセージを作成
const buildValidationToastMessages = (errors) => {
  const userMessages = Object.values(errors.users || {}).flatMap((userError) =>
    Object.values(userError),
  );

  const messages = [...(errors.base ? [errors.base] : []), ...userMessages];

  return [...new Set(messages.filter(Boolean))];
};

// API送信用に整形
const buildCompanyUsersPayload = (editedCompanyUsers) =>
  editedCompanyUsers.map((companyUser) => ({
    id: companyUser.id,
    role: companyUser.role || "general",
    email: companyUser.email || "",
    last_name: companyUser.last_name || "",
    first_name: companyUser.first_name || "",
    last_name_kana: companyUser.last_name_kana || "",
    first_name_kana: companyUser.first_name_kana || "",
    _destroy: companyUser._destroy || false,
  }));

/**
 * 企業担当者情報の展開行
 */
export default function CompanyExpandRow({
  company,
  isOpen,
  onSave,
  onDelete,
  fieldErrorsByCompany,
  clearCompanyFieldErrors,
}) {
  // 編集中の担当者一覧
  const [editedCompanyUsers, setEditedCompanyUsers] = useState(() =>
    buildInitialCompanyUsers(company),
  );

  // エラー表示用
  const [localErrors, setLocalErrors] = useState({
    users: {},
    role: null,
    base: null,
    ...(fieldErrorsByCompany[company.id] || {}),
  });

  // 担当者削除確認モーダル用の対象ID
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    setLocalErrors((prev) => ({
      ...prev,
      role: null,
      base: null,
    }));
  }, [fieldErrorsByCompany]);

  // 画面上に表示する担当者
  const visibleCompanyUsers = useMemo(
    () => editedCompanyUsers.filter((companyUser) => !companyUser._destroy),
    [editedCompanyUsers],
  );

  // APIのエラー（indexベース）を userId ベースに変換する
  useEffect(() => {
    if (!fieldErrorsByCompany[company.id]?.users) return;

    const mappedErrors = {};

    // index付きエラーを userId ベースに変換
    Object.entries(fieldErrorsByCompany[company.id].users).forEach(
      ([index, userErrors]) => {
        const user = editedCompanyUsers[Number(index)];

        // 表示対象のみエラー反映
        if (user && !user._destroy) {
          mappedErrors[user.id] = userErrors;
        }
      },
    );

    setLocalErrors((prev) => ({
      ...prev,
      users: mappedErrors,
    }));
  }, [fieldErrorsByCompany, editedCompanyUsers]);

  // 編集内容を初期状態へ戻す
  useEffect(() => {
    setEditedCompanyUsers(buildInitialCompanyUsers(company));
    setDeleteTargetId(null);
  }, [company.id, company.company_users]);

  // エラーをクリアする
  const clearErrors = useCallback(() => {
    setLocalErrors({
      users: {},
      role: null,
      base: null,
    });

    // 親が持っているAPIエラーもクリア
    clearCompanyFieldErrors?.(company.id);
  }, [clearCompanyFieldErrors, company.id]);

  // 保存成功後の処理
  const afterSaveSuccess = useCallback(() => {
    clearErrors();
  }, [clearErrors]);

  // 削除確認モーダルに表示する対象ユーザー
  const deleteTargetUser = useMemo(
    () =>
      editedCompanyUsers.find(
        (companyUser) => companyUser.id === deleteTargetId,
      ),
    [editedCompanyUsers, deleteTargetId],
  );

  // 現在の代表者（削除予定を除く）
  const currentRepresentative = useMemo(
    () =>
      visibleCompanyUsers.find((companyUser) => companyUser.role === "admin"),
    [visibleCompanyUsers],
  );

  const currentRepresentativeName = currentRepresentative
    ? buildFullName(
        currentRepresentative.last_name,
        currentRepresentative.first_name,
      )
    : "-";

  const currentRepresentativeEmail = currentRepresentative?.email || "-";

  // 担当者情報を更新
  const updateCompanyUser = (companyUserId, updater) => {
    setEditedCompanyUsers((prev) =>
      prev.map((companyUser) =>
        companyUser.id === companyUserId ? updater(companyUser) : companyUser,
      ),
    );
  };

  // フィールド更新
  const updateCompanyUserField = (companyUserId, key, value) => {
    updateCompanyUser(companyUserId, (companyUser) => ({
      ...companyUser,
      [key]: value,
    }));
  };

  // 個別フィールドエラーだけ消す
  const clearFieldError = (userId, fieldName) => {
    setLocalErrors((prev) => {
      const newErrors = {
        ...prev,
        users: { ...(prev.users || {}) },
      };

      if (newErrors.users?.[userId]) {
        newErrors.users[userId] = { ...newErrors.users[userId] };
        delete newErrors.users[userId][fieldName];

        if (Object.keys(newErrors.users[userId]).length === 0) {
          delete newErrors.users[userId];
        }
      }

      return newErrors;
    });
  };

  // テキスト入力系の更新
  const handleFieldChange = (companyUserId, fieldName, value) => {
    updateCompanyUserField(companyUserId, fieldName, value);
    clearFieldError(companyUserId, fieldName);
  };

  // 役割変更時は全体エラーも消す
  const handleRoleChange = (companyUserId, value) => {
    updateCompanyUserField(companyUserId, "role", value);

    setLocalErrors((prev) => ({
      ...prev,
      role: null,
      base: null,
    }));
  };

  // 削除予定に変更（即削除しない）
  const handleDeleteCompanyUser = (companyUserId) => {
    updateCompanyUser(companyUserId, (companyUser) => ({
      ...companyUser,
      _destroy: true,
    }));

    setLocalErrors((prev) => {
      const nextUsers = { ...(prev.users || {}) };
      delete nextUsers[companyUserId];

      return {
        ...prev,
        users: nextUsers,
        role: null,
        base: null,
      };
    });
  };

  // バリデーションエラーを表示する
  const showValidationErrors = (errors) => {
    setLocalErrors(errors);

    const messages = buildValidationToastMessages(errors);

    // トースト表示（1つにまとめる）
    if (messages.length > 0) {
      toast.error(
        <div>
          {messages.map((message, index) => (
            <div key={index} style={{ marginBottom: 4 }}>
              {message}
            </div>
          ))}
        </div>,
      );
    }
  };

  // バリデーションエラーがあるか判定する
  const hasValidationErrors = (errors) =>
    Object.keys(errors.users || {}).length > 0 || errors.base || errors.role;

  // 保存
  const handleSave = async (e) => {
    e.stopPropagation();

    // バリデーション実行
    const errors = validateCompanyUsers(editedCompanyUsers);

    // エラーが1つでもあれば処理を止める
    if (hasValidationErrors(errors)) {
      showValidationErrors(errors);
      return;
    }

    const isSaved = await onSave?.(company.id, {
      company_users_attributes: buildCompanyUsersPayload(editedCompanyUsers),
    });

    if (isSaved) {
      afterSaveSuccess();
    }
  };

  // キャンセル
  const handleCancel = (e) => {
    e.stopPropagation();
    setEditedCompanyUsers(buildInitialCompanyUsers(company));
    setDeleteTargetId(null);
    clearErrors();
  };

  return (
    <TableRow>
      <TableCell
        colSpan={8}
        sx={{
          p: 0,
          borderBottom: isOpen ? 1 : 0,
          width: "100%",
        }}
      >
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
              px: 3,
              py: 2.5,
              backgroundColor: "#FAFBFC",
              borderTop: "1px solid #ECEFF3",
            }}
          >
            <CompanyUserSummary
              representativeName={currentRepresentativeName}
              representativeEmail={currentRepresentativeEmail}
            />

            <CompanyUserList
              companyId={company.id}
              visibleCompanyUsers={visibleCompanyUsers}
              localErrors={localErrors}
              onFieldChange={handleFieldChange}
              onRoleChange={handleRoleChange}
              onDeleteClick={setDeleteTargetId}
            />
            <Box
              sx={{
                mt: 2.5,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <ActionButton
                label="キャンセル"
                onClick={handleCancel}
                sx={{
                  ...buttonBaseSx,
                  backgroundColor: "#FFFFFF",
                  color: "#374151",
                  border: "1px solid #D1D5DB",
                  "&:hover": {
                    backgroundColor: "#F9FAFB",
                  },
                }}
              />

              <ActionButton
                label="保存"
                onClick={handleSave}
                sx={{
                  ...buttonBaseSx,
                  backgroundColor: JOB_COLOR,
                  "&:hover": { backgroundColor: JOB_COLOR_DARK },
                }}
              />

              <ActionButton
                label="削除"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(company.id);
                }}
                sx={{
                  ...buttonBaseSx,
                  backgroundColor: "error.main",
                  "&:hover": { backgroundColor: "error.dark" },
                }}
              />
            </Box>

            <ConfirmStopModal
              open={!!deleteTargetId}
              onClose={() => setDeleteTargetId(null)}
              onConfirm={() => {
                handleDeleteCompanyUser(deleteTargetId);
                setDeleteTargetId(null);
              }}
              title="担当者を削除"
              message={
                deleteTargetUser
                  ? `${buildFullName(
                      deleteTargetUser.last_name,
                      deleteTargetUser.first_name,
                    )} を削除しますか？保存するまで削除は確定されません。`
                  : "担当者を削除しますか？保存するまで削除は確定されません。"
              }
              confirmLabel="削除する"
            />
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}
